'use client';

import { Icon } from '@magicschool/spellbook/components/Icon';
import { Label } from '@magicschool/spellbook/components/Label';
import { Text } from '@magicschool/spellbook/components/Text';
import { useId } from '@magicschool/spellbook/hooks/useId';
import { cn } from '@magicschool/spellbook/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import * as RadioGroupPrimitive from 'radix-ui/radio-group';

export type RadioGroupItemProps = React.ComponentProps<typeof RadioGroupPrimitive.Item> &
  VariantProps<typeof radioGroupItemVariants> & {
    label: string | React.ReactNode;
    showLabel?: boolean;
    showDescription?: boolean;
    value: string;
    description?: string;
    invalid?: boolean;
    style?: React.CSSProperties;
    readOnly?: boolean;
  };

export const radioGroupItemVariants = cva(
  [
    'peer',
    'group/radio-icon',
    'size-4',
    'm-1',
    'shrink-0',
    'rounded-full',
    'outline-none',
    'cursor-pointer',
    'fade-in',
    'transition-all',
    'border-[1.5px]',
    'border-checkbox',
    'bg-surface-background-0',
    'hover:bg-surface-background-2',
    'hover:border-checkbox-hover',
    'group-data-[readOnly=true]:hover:bg-surface-background-0',
    'group-data-[readOnly=true]:cursor-default',
    'group-data-[readOnly=true]:hover:checkbox-hover',
    'active:bg-surface-background-2',
    'focus-visible-ring',
    'aria-invalid:!border-error-stroke',
    'aria-invalid:hover:!border-error-stroke',
    'disabled:cursor-not-allowed',
    'disabled:border-disabled-background',
    'disabled:bg-disabled-background',
    'data-[state=checked]:disabled:bg-disabled-background',
    'data-[state=checked]:disabled:border-disabled-background',
    'data-[state=checked]:disabled:text-disabled-foreground',
    'aspect-square',
  ],
  {
    variants: {
      variant: {
        default: [
          'data-[state=checked]:border-primary-solid-background',
          'data-[state=checked]:text-primary-solid-background',
          'data-[state=checked]:border-primary-solid-background-hover',
          'data-[state=checked]:hover:text-primary-solid-background-hover',
          'data-[state=checked]:active:border-primary-solid-background-active',
          'data-[state=checked]:active:text-primary-solid-background-active',
        ],
        checked: [
          'data-[state=checked]:border-success-icon',
          'data-[state=checked]:hover:border-success-icon-hover',
          'group-data-[readOnly=true]:data-[state=checked]:hover:border-success-icon',
          'disabled:data-[state=checked]:border-success-icon',
          'disabled:data-[state=checked]:bg-surface-background-0',
        ],
        incorrect: [
          'data-[state=checked]:border-warning-icon',
          'data-[state=checked]:hover:border-warning-icon-hover',
          'group-data-[readOnly=true]:data-[state=checked]:hover:border-warning-icon',
          'disabled:data-[state=checked]:border-warning-icon',
          'disabled:data-[state=checked]:bg-surface-background-0',
        ],
      },
      readOnly: {
        true: 'cursor-default',
        false: '',
      },
    },
  },
);

export const radioGroupItemDescriptionVariants = cva('flex flex-col items-start justify-start mt-0.75', {
  variants: {
    showLabel: {
      false: 'sr-only',
    },
  },
});

export const RadioGroupItem: React.FC<RadioGroupItemProps> = ({
  className,
  label,
  showLabel = true,
  showDescription = true,
  invalid,
  description,
  id: providedId,
  variant = 'default',
  style,
  readOnly = false,
  ...props
}) => {
  const id = useId(providedId);
  const descriptionId = `${id}-description`;
  const labelId = `${id}-label`;

  return (
    <div
      className="flex group items-start justify-start overflow-hidden gap-1.5 min-h-6"
      data-disabled={props.disabled}
      data-readonly={readOnly}
      style={style}
    >
      <RadioGroupPrimitive.Item
        data-spellbook-name="radio-group-item"
        className={cn(radioGroupItemVariants({ variant, readOnly }), className)}
        id={id}
        {...(invalid ? { 'aria-invalid': true } : {})}
        {...(description ? { 'aria-describedby': descriptionId } : {})}
        {...(label ? { 'aria-labelledby': labelId } : {})}
        {...props}
      >
        <RadioGroupPrimitive.Indicator
          data-spellbook-name="radio-group-indicator"
          className="flex items-center justify-center transition-all"
        >
          {variant === 'default' && <span className="size-2 min-w-2 min-h-2 bg-current rounded-full" />}
          {(variant === 'checked' || variant === 'incorrect') && (
            <Icon
              name={variant === 'checked' ? 'check' : 'x'}
              size="sm"
              strokeWidth={4}
              color="inherit"
              className={cn(
                ['min-h-2', 'transition-all', 'h-2', 'w-2', 'min-w-2', 'group-data-[disabled=true]/radio-icon:text-disabled-foreground'],
                variant === 'checked'
                  ? [
                      '!text-success-icon',
                      'group-hover/radio-icon:text-success-icon-hover',
                      'group-hover/radio-icon:group-data-[readOnly=true]:text-success-icon',
                    ]
                  : [
                      '!text-warning-icon',
                      'group-hover/radio-icon:text-warning-icon-hover',
                      'group-hover/radio-icon:group-data-[readOnly=true]:text-warning-icon',
                    ],
              )}
            />
          )}
        </RadioGroupPrimitive.Indicator>
      </RadioGroupPrimitive.Item>
      {label && typeof label === 'string' && (
        <div className={radioGroupItemDescriptionVariants({ showLabel })}>
          <Label
            className="cursor-pointer group-data-[disabled=true]:!text-current group-data-[disabled=true]:!cursor-default"
            htmlFor={id}
            id={labelId}
          >
            {label}
          </Label>
          {description && (
            <Text
              color="default-soft"
              className={showDescription ? undefined : 'sr-only'}
              variant="body-2"
              element="span"
              id={descriptionId}
            >
              {description}
            </Text>
          )}
        </div>
      )}
      {label && typeof label !== 'string' && (
        <label className="cursor-pointer group-data-[disabled=true]:!cursor-default" htmlFor={id} id={labelId}>
          {label}
        </label>
      )}
    </div>
  );
};
